// School-admin overview home. Greeting + at-a-glance counts + quick links to
// the three management screens. docs/replan-v2.md R9.

import { useT } from '@fajrarabi/i18n/react';
import { Button, Card, CardBody } from '@fajrarabi/ui';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';

import { useAuth } from '~/lib/auth';
import { asLocale } from '~/lib/locale';
import { listClasses, listStudents, listTeachers } from '~/lib/school';

export function SchoolAdminHome() {
  const t = useT();
  const { user } = useAuth();
  const { locale: rawLocale } = useParams({ from: '/$locale' });
  const locale = asLocale(rawLocale);
  const name = user?.displayName ?? user?.email ?? '';

  const teachers = useQuery({
    queryKey: ['school', 'teachers'],
    queryFn: ({ signal }) => listTeachers(signal),
  });
  const classes = useQuery({
    queryKey: ['school', 'classes'],
    queryFn: ({ signal }) => listClasses(signal),
  });
  const students = useQuery({
    queryKey: ['school', 'students'],
    queryFn: ({ signal }) => listStudents(signal),
  });

  return (
    <div className="mx-auto max-w-4xl px-6 py-10">
      <h1 className="text-2xl font-bold text-ink-900">{t('app.school.welcome', { name })}</h1>
      <p className="mt-1 text-ink-700">{t('app.school.overview_subtitle')}</p>

      <div className="mt-8 grid gap-4 sm:grid-cols-3">
        <StatLink
          to="/$locale/school/teachers"
          locale={locale}
          label={t('app.school.stat_teachers')}
          value={teachers.data?.length}
          icon="👩‍🏫"
        />
        <StatLink
          to="/$locale/school/classes"
          locale={locale}
          label={t('app.school.stat_classes')}
          value={classes.data?.length}
          icon="📚"
        />
        <StatLink
          to="/$locale/school/students"
          locale={locale}
          label={t('app.school.stat_students')}
          value={students.data?.length}
          icon="🧑‍🎓"
        />
      </div>

      <div className="mt-8 grid gap-4 sm:grid-cols-2">
        <Card>
          <CardBody>
            <p className="text-lg font-bold text-ink-900">{t('app.school.teachers_title')}</p>
            <p className="mt-1 text-sm text-ink-700">{t('app.school.overview_subtitle')}</p>
            <Link to="/$locale/school/teachers" params={{ locale }} className="mt-5 inline-block">
              <Button>{t('app.school.invite_teacher')}</Button>
            </Link>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <p className="text-lg font-bold text-ink-900">{t('app.school.classes_title')}</p>
            <p className="mt-1 text-sm text-ink-700">{t('app.school.overview_subtitle')}</p>
            <Link to="/$locale/school/classes" params={{ locale }} className="mt-5 inline-block">
              <Button variant="secondary">{t('app.school.add_class')}</Button>
            </Link>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}

function StatLink({
  to,
  locale,
  label,
  value,
  icon,
}: {
  to: '/$locale/school/teachers' | '/$locale/school/classes' | '/$locale/school/students';
  locale: 'ar' | 'en';
  label: string;
  value: number | undefined;
  icon: string;
}) {
  return (
    <Link
      to={to}
      params={{ locale }}
      className="rounded-2xl bg-white p-5 shadow-[0_12px_30px_-20px_rgba(31,26,23,0.25)] ring-1 ring-burgundy-500/10 transition-shadow hover:shadow-[0_18px_40px_-22px_rgba(31,26,23,0.3)]"
    >
      <div className="flex items-center justify-between">
        <span className="text-sm text-ink-500">{label}</span>
        <span aria-hidden="true" className="text-xl">
          {icon}
        </span>
      </div>
      <p className="mt-2 text-3xl font-bold text-burgundy-700">{value ?? '—'}</p>
    </Link>
  );
}
